import { useMemo } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { useMyChallenges } from "@/features/queries";

// Mirror of `DAILY_CHALLENGE_LIMIT` in backend/app/services/challenge_notifications.py.
// Day boundary is UTC midnight to match backend/app/db_utils.get_start_of_day_utc,
// so the count the user sees here lines up with the count the API enforces.
export const DAILY_CHALLENGE_LIMIT = 3;

export interface DailyChallengeLimit {
  used: number;
  limit: number;
  remaining: number;
  atLimit: boolean;
}

/** Counts how many challenges the current user has thrown today (UTC). */
export function useDailyChallengeLimit(): DailyChallengeLimit {
  const { user } = useAuth();
  const { data: myChallenges = [] } = useMyChallenges();

  return useMemo(() => {
    const startOfDayUtc = new Date();
    startOfDayUtc.setUTCHours(0, 0, 0, 0);
    const startMs = startOfDayUtc.getTime();

    const used = user
      ? myChallenges.filter(
          (c) =>
            c.challenger.id === user.id &&
            new Date(c.created_at).getTime() >= startMs,
        ).length
      : 0;

    const remaining = Math.max(0, DAILY_CHALLENGE_LIMIT - used);
    return {
      used,
      limit: DAILY_CHALLENGE_LIMIT,
      remaining,
      atLimit: used >= DAILY_CHALLENGE_LIMIT,
    };
  }, [user, myChallenges]);
}
